import type {CampusLocation} from "@repo/actions/AcademyApi/GetCampuses";
import type {CampusWithDistance} from "../location";

export const UNKNOWN_REGION_LABEL = "Unknown Region";

export type CampusRegionGroup = {
	region: string;
	campuses: CampusWithDistance[];
};

/**
 * Groups campuses by region, preserving input order within each group.
 * Region headers are sorted alphabetically; campuses without a region go last
 * under `unknownRegionLabel` (defaults to {@link UNKNOWN_REGION_LABEL}).
 */
export function groupCampusesByRegion(
	campuses: CampusWithDistance[],
	unknownRegionLabel: string = UNKNOWN_REGION_LABEL
): CampusRegionGroup[] {
	const groupsByRegion = new Map<string, CampusWithDistance[]>();
	const unknownCampuses: CampusWithDistance[] = [];

	// Group campuses by region
	for (const entry of campuses) {
		const region = entry.campus.region?.trim();
		if (!region) {
			unknownCampuses.push(entry);
			continue;
		}

		const existing = groupsByRegion.get(region);
		if (existing) {
			existing.push(entry);
		} else {
			groupsByRegion.set(region, [entry]);
		}
	}

	const groups = [...groupsByRegion.entries()]
		.map(([region, regionCampuses]) => ({region, campuses: regionCampuses}))
		.sort((a, b) => a.region.localeCompare(b.region));

	// Add unknown campuses group
	if (unknownCampuses.length > 0) {
		groups.push({
			region: unknownRegionLabel,
			campuses: unknownCampuses,
		});
	}

	return groups;
}

/**
 * Single-line display string for a campus street address, or null when missing.
 */
export function formatCampusAddress(campus: CampusLocation): string | null {
	const address = campus.address;
	if (!address) {
		return null;
	}

	const streetLine = [address.street, address.street2]
		.filter((part): part is string => Boolean(part && part.length > 0))
		.join(", ");
	const cityStateZip = [
		address.city,
		[address.state, address.zip].filter((part) => part.length > 0).join(" "),
	]
		.filter((part) => part.length > 0)
		.join(", ");

	const formatted = [streetLine, cityStateZip]
		.filter((part) => part.length > 0)
		.join(", ");

	return formatted.length > 0 ? formatted : null;
}

const GOOGLE_MAPS_SEARCH_URL =
	"https://www.google.com/maps/search/?api=1&query=";

/**
 * Google Maps search URL for a campus.
 * Uses `AoPS Academy {short_name}, {address_google}` so Maps opens the named
 * place rather than a bare street address. Falls back to lat/lon, then street
 * address, when address_google is missing.
 */
export function getCampusMapUrl(campus: CampusLocation): string | null {
	const addressGoogle = campus.address_google?.trim();
	const shortName = campus.short_name?.trim();
	if (addressGoogle && shortName) {
		const query = `AoPS Academy ${shortName}, ${addressGoogle}`;
		return `${GOOGLE_MAPS_SEARCH_URL}${encodeURIComponent(query)}`;
	}

	if (campus.latitude != null && campus.longitude != null) {
		const query = encodeURIComponent(`${campus.latitude},${campus.longitude}`);
		return `${GOOGLE_MAPS_SEARCH_URL}${query}`;
	}

	const formatted = formatCampusAddress(campus);
	if (!formatted) {
		return null;
	}

	return `${GOOGLE_MAPS_SEARCH_URL}${encodeURIComponent(formatted)}`;
}

/**
 * Formats miles from a search origin to one decimal place (number only).
 */
export function formatDistanceMiles(miles: number): string {
	return (Math.round(miles * 10) / 10).toFixed(1);
}
